'use client'

import {FormEvent, MouseEvent, useEffect, useRef, useState} from 'react'
import Image from 'next/image'
import Link from 'next/link'
import {useRouter} from 'next/navigation'
import Avatar from '@/components/Avatar'
import styles from '@/components/Header/Header.module.scss'
import useSearchHistory from '@hooks/header/search/useSearchHistory'
import useDebounce from '@hooks/helperHooks/useDebounce'
import {removeSearch, setKeepHistory} from '@services/header/search'
import {generateSearchUrl} from '@utils/helpers'

const HeaderSearch = () => {
  const router = useRouter()
  const [openMenu, setOpenMenu] = useState<boolean>(false)
  const [searchKeyWord, setSearchKeyWord] = useState<string>('')
  const debouncedSearchKeyword = useDebounce(searchKeyWord, 500)
  const {searchHistory, suggestBj, keepHistory, refetchSearchHistory} =
    useSearchHistory({
      getData: openMenu,
      textSearch: debouncedSearchKeyword,
    })

  const searchBoxRef = useRef<any>(null)

  const handleSubmitSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const keyword = e.currentTarget?.search.value
    if (keyword?.length > 0) {
      router.push(generateSearchUrl(keyword))
      setOpenMenu(false)
    }
  }

  const handleSearchBoxFocus = () => {
    if (!openMenu) {
      setOpenMenu(true)
    }
  }

  const handleClickOutside = (event: any) => {
    if (searchBoxRef.current && !searchBoxRef.current?.contains(event.target)) {
      setOpenMenu(false)
    }
  }

  const handleKeepSearchHistory = () => {
    setKeepHistory({
      status: keepHistory ? 'off' : 'on',
    }).then((response: any) => {
      if (response?.result === 1) {
        refetchSearchHistory()
      }
    })
  }

  const handleSearchInputChange = (e: any) => {
    if (!openMenu) {
      setOpenMenu(true)
    }
    setSearchKeyWord(e.target.value)
  }

  const handleRemoveSearch = ({
    removeType,
    key,
    textSearch,
  }: {
    removeType: number
    key?: string
    textSearch?: string
  }) => {
    removeSearch({removeType, key, textSearch}).then((response: any) => {
      if (response?.result === 1) {
        refetchSearchHistory()
      }
    })
  }

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true)

    return () => {
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [])

  return (
    <div
      className={styles.searchBox + ' ' + styles.hideOnMobile}
      ref={searchBoxRef}
    >
      <form
        onSubmit={handleSubmitSearch}
        className={openMenu ? 'active' : ''}
        onClick={handleSearchBoxFocus}
        // style={{height: '26px'}}
      >
        <input
          type="text"
          name="search"
          aria-label="Tìm kiếm"
          placeholder=""
          autoComplete="off"
          onFocus={handleSearchBoxFocus}
          onChange={handleSearchInputChange}
        />
        <Image
          width={20}
          height={20}
          src={
            process.env.NEXT_PUBLIC_PATH_DOMAIN +
            'images/icons/search-normal.svg'
          }
          alt="Tìm kiếm"
        />
      </form>
      {openMenu && (
        <div className={styles.searchSuggest}>
          <div className={styles.searchSuggestList}>
            {suggestBj.length > 0
              ? suggestBj.map((item) => (
                  <Link
                    key={item?.user_id}
                    href={generateSearchUrl(item?.user_nick)}
                    onClick={() => setOpenMenu(false)}
                  >
                    <Avatar src={item?.station_logo} width={20} height={20} />
                    <span>{item?.user_nick}</span>
                  </Link>
                ))
              : keepHistory && searchHistory.length > 0
              ? searchHistory.map((item) => (
                  <Link
                    key={item?.key}
                    href={generateSearchUrl(item?.key)}
                    onClick={() => setOpenMenu(false)}
                  >
                    <Image
                      src={
                        process.env.NEXT_PUBLIC_PATH_DOMAIN +
                        'images/icons/search-status.svg'
                      }
                      width={12}
                      height={12}
                      alt=""
                    />
                    <span>{item?.key}</span>
                    <button
                      onClick={(e: MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation()
                        e.preventDefault()
                        handleRemoveSearch({
                          removeType: 0,
                          key: item?.key,
                          textSearch: item?.key,
                        })
                      }}
                    >
                      <Image
                        src={
                          process.env.NEXT_PUBLIC_PATH_DOMAIN +
                          'images/icons/close.svg'
                        }
                        alt=""
                        width={20}
                        height={20}
                      />
                    </button>
                  </Link>
                ))
              : keepHistory && (
                  <div className={styles.searchSuggestBlank}>
                    <b>
                      {debouncedSearchKeyword.length > 0
                        ? 'Không có kết quả tìm kiếm.'
                        : 'Không có cụm từ tìm kiếm nào gần đây.'}
                    </b>
                  </div>
                )}
            {!keepHistory &&
              (debouncedSearchKeyword.length === 0 ? (
                <div className={styles.searchSuggestBlank}>
                  <span style={{marginRight: 5}}>Lưu các cụm từ tìm kiếm</span>
                  <b>đã tắt.</b>
                </div>
              ) : (
                suggestBj.length === 0 && (
                  <div className={styles.searchSuggestBlank}>
                    Không có kết quả tìm kiếm.
                  </div>
                )
              ))}
          </div>
          <div className={styles.searchSuggestFooter}>
            <div className={styles.rememberKeywords}>
              <span>Lưu các cụm từ tìm kiếm</span>
              <button
                className={`${styles.rememberKeywordsSwitch}${
                  keepHistory ? ' active' : ''
                }`}
                onClick={handleKeepSearchHistory}
              >
                {keepHistory ? 'ON' : 'OFF'}
              </button>
            </div>
            <button
              className={styles.btnDeleteSearchHistory}
              onClick={() => {
                if (searchHistory.length > 0) {
                  handleRemoveSearch({
                    removeType: 1,
                  })
                }
              }}
            >
              Xóa tất cả
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default HeaderSearch
